import Head from 'next/head'
import React from 'react'

import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@wartek-id/tabs'
import { Text } from '@wartek-id/text'

import { useGetPlaylists } from 'api/video-repository/useAPIPlaylists'
import { PlaylistCardShimmer } from 'app/VideoRepository/components/Preloader'
import Playlists from 'app/VideoRepository/Playlists'
import { useVideoInspirasiDesktopView } from 'app/VideoRepository/hooks/useVideoInspirasiDesktopView'
import PageContainer from 'app/DocumentCollectionPage/components/PageContainer'
import DesktopPageHeader from 'components/DesktopPageHeader'
import { EmptyState, EmptyStateAction } from 'components/EmptyState'
import IdePraktikHeader from 'components/IdePraktikHeader'
import PrefetchLink from 'components/PrefetchLink'
import { APP_LOGIN_STORAGE_KEY } from 'configs/auth'
import AndroidHandler from 'utils/androidHandler'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'
import { REFERRER_KEY } from 'utils/hooks/useRouterHistory'
import {
  useClickTracker,
  usePageViewTracker,
  useScrollTracker,
} from 'utils/satu-event-tracker'

const HEADER_TITLE = 'Yuk, Temukan Inspirasi Mengajar Hari Ini!'
const HEADER_SUBTITLE =
  'Praktik baik terpilih untuk membantu Bapak/Ibu menghadirkan Pembelajaran yang bermakna, berkesadaran, dan menggembirakan bagi murid'
const TRACKER_SCREEN_NAME = 'VIDEO_INSPIRASI'
const HELPDESK_LINK = 'https://pusatinformasi.guru.kemdikbud.go.id'

const IdePraktikPage = () => {
  const androidHandler = AndroidHandler()
  const [session] = useLocalStorage(APP_LOGIN_STORAGE_KEY, null)
  const [, , , getReferrerValue] = useLocalStorage(REFERRER_KEY, null)
  const referrer = getReferrerValue()
  const { isDesktop } = useVideoInspirasiDesktopView()
  const trackTabClick = useClickTracker({
    activityType: 'CLICK_TAB_CERITA_PRAKTIK',
    defaultAttributes: { screen_name: TRACKER_SCREEN_NAME },
  })
  const { data, isSuccess, isError, isLoading } = useGetPlaylists()

  usePageViewTracker({
    activityType: 'PAGE_VIEW_VIDEO_INSPIRASI',
    screenName: TRACKER_SCREEN_NAME,
    attributes: {
      is_webview: [androidHandler.enabled, 'bool'],
    },
  })

  const scrollElement = React.useRef<HTMLDivElement>(null)
  useScrollTracker({
    activityType: 'SCROLL_TAB_VIDEO_INSPIRASI',
    scrollElement: isDesktop ? null : scrollElement,
    attributes: {
      screen_name: TRACKER_SCREEN_NAME,
    },
  })

  if (isDesktop) {
    return (
      <>
        <Head>
          <title>Ide Praktik - Ruang GTK</title>
        </Head>

        <div className="min-h-screen bg-white">
          <DesktopPageHeader
            navLinks={[
              { label: 'Bantuan', href: HELPDESK_LINK, external: true },
            ]}
            breadcrumbItems={[{ label: 'Ide Praktik', href: null }]}
            breadcrumbClassName="bg-[#0036a3] !border-b-0"
            breadcrumbTextClassName="[&_a]:!text-white/70 [&_span]:!text-white"
            bottomBar={
              <>
                {/* Hero section */}
                <div className="bg-[#0036a3]">
                  <PageContainer className="!py-6">
                    <div className="flex items-center gap-4">
                      <img
                        src="/icon/ide-praktik.svg"
                        alt=""
                        width={60}
                        height={60}
                      />
                      <div>
                        <h1 className="text-white text-lg font-semibold">
                          {HEADER_TITLE}
                        </h1>
                        <p className="text-white text-sm mt-1 opacity-90">
                          {HEADER_SUBTITLE}
                        </p>
                      </div>
                    </div>
                  </PageContainer>
                </div>

                {/* Tabs */}
                <div className="bg-white border-b border-gray-200">
                  <PageContainer className="!py-0">
                    <Tabs
                      withBorder
                      selectedIndex={1}
                      onChange={(index) => {
                        if (index !== 0) return
                        window.location.href = '/ide-praktik'
                      }}
                    >
                      <TabList
                        style={{
                          borderBottom: '1px solid var(--color-border-subdued)',
                        }}
                      >
                        <Tab
                          onClick={() => {
                            trackTabClick(undefined, { keepAlive: true })
                          }}
                        >
                          <PrefetchLink
                            className="flex items-center justify-center w-full"
                            href="/ide-praktik"
                          >
                            Cerita Praktik
                          </PrefetchLink>
                        </Tab>
                        <Tab>Video Inspirasi</Tab>
                      </TabList>
                    </Tabs>
                  </PageContainer>
                </div>
              </>
            }
          />
          <main>
            <div className="bg-surface-subdued">
              <PageContainer>
                {isLoading && (
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, index) => (
                      <PlaylistCardShimmer key={index} />
                    ))}
                  </div>
                )}
                {isSuccess && (
                  <>
                    <Text variant="heading-sm" className="mb-4">
                      Video Pilihan
                    </Text>
                    <Playlists
                      playlists={data.data}
                      screenName={TRACKER_SCREEN_NAME}
                      isDesktop
                    />
                  </>
                )}
                {isError && (
                  <EmptyState
                    title={'Gagal mengambil data'}
                    description={'Video Repository tidak dapat diakses'}
                  >
                    <EmptyStateAction onClick={() => window.location.reload()}>
                      Muat Ulang
                    </EmptyStateAction>
                  </EmptyState>
                )}
              </PageContainer>
            </div>
          </main>
        </div>
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Ide Praktik - Ruang GTK</title>
      </Head>

      <div>
        <IdePraktikHeader
          title={HEADER_TITLE}
          subtitle={HEADER_SUBTITLE}
          onBack={() => {
            if (androidHandler.enabled) {
              androidHandler.backToApp()
            } else {
              if (!!session && referrer === '') {
                window.location.href = `${window.location.origin}/home`
              } else {
                window.history.back()
              }
            }
          }}
        />
        <Tabs
          isFitted
          withBorder
          selectedIndex={1}
          onChange={(index) => {
            if (index !== 0) return
            window.location.href = '/ide-praktik'
          }}
        >
          <TabList
            style={{ borderBottom: '1px solid var(--color-border-subdued)' }}
          >
            <Tab
              onClick={() => {
                trackTabClick(undefined, { keepAlive: true })
              }}
            >
              <PrefetchLink
                className="flex items-center justify-center w-full"
                href="/ide-praktik"
              >
                Cerita Praktik
              </PrefetchLink>
            </Tab>
            <Tab>Video Inspirasi</Tab>
          </TabList>
          <TabPanels>
            <div className="bg-gray-5">
              <TabPanel>Cerita Praktik Konten</TabPanel>
              <TabPanel>
                <div
                  ref={scrollElement}
                  className="p-4 overflow-auto max-h-[calc(100vh-240px)] scrollbar-hide-auto"
                >
                  {isLoading &&
                    [...Array(4)].map((_, index) => (
                      <PlaylistCardShimmer key={index} />
                    ))}
                  {isSuccess && (
                    <>
                      <Text variant="heading-sm" className="mb-4">
                        Video Pilihan
                      </Text>
                      <Playlists
                        playlists={data.data}
                        screenName={TRACKER_SCREEN_NAME}
                      />
                    </>
                  )}
                </div>
                {isError && (
                  <EmptyState
                    title={'Gagal mengambil data'}
                    description={'Video Repository tidak dapat diakses'}
                  >
                    <EmptyStateAction onClick={() => window.location.reload()}>
                      Muat Ulang
                    </EmptyStateAction>
                  </EmptyState>
                )}
              </TabPanel>
            </div>
          </TabPanels>
        </Tabs>
      </div>
    </>
  )
}

IdePraktikPage.Layout = ({ children }) => children

export default IdePraktikPage
